import React, { useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { organizerApi } from '../../../modules/services/organizerService';
import translate from '../../../i18n/translate';

interface Props {
  organizerId: number;
}

const OrganizerInfoTooltip: React.FC<Props> = ({ organizerId }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [triggerFetch, { data, isFetching }] =
    organizerApi.useLazyFetchOrganizersQuery();

  const organizer = data?.list?.[0];

  const handleOpen = () => {
    setOpen(true);
    if (!data && !isFetching) {
      triggerFetch({ type: 'all', organizerId, skip: 0, take: 1, filter: '' });
    }
  };

  const handleClick = () => {
    setOpen(false);
    navigate(`/organizers/all?organizerId=${organizerId}`);
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 280 }}>
      {isFetching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          <CircularProgress size={20} color="warning" />
        </Box>
      )}
      {!isFetching && !organizer && (
        <Typography variant="body2">{translate('marketingPersonsSearchNoResult')}</Typography>
      )}
      {!isFetching && organizer && (
        <>
          <Typography variant="body2" fontWeight="bold" color="warning.main" sx={{ mb: 0.5 }}>
            {organizer.name} {organizer.surname} (ID: {organizer.id})
          </Typography>
          <Typography variant="caption">{translate('gridOrganizersTotalEventsCount')} {organizer.eventsCount ?? 0}</Typography>
          <Typography variant="caption">{translate('gridOrganizersActiveEventsCount')} {organizer.activeEventsCount ?? 0}</Typography>
          <Typography variant="caption">{translate('gridOrganizersFinishedEventsCount')} {organizer.finishedEventsCount ?? 0}</Typography>
          <Typography variant="caption">{translate('gridOrganizersCancelledEventsCount')} {organizer.cancelledEventsCount ?? 0}</Typography>
          <Typography variant="caption">{translate('gridOrganizersPendingEventsCount')} {organizer.pendingEventsCount ?? 0}</Typography>
          <Box sx={{ borderTop: '1px dashed rgba(255,255,255,0.3)', my: 0.5 }} />
          <Typography variant="caption">{translate('gridOrganizersTotalTicketsSoldCount')} {organizer.totalTicketSoldCount ?? 0}</Typography>
          <Typography variant="caption">{translate('gridOrganizersTotalTicketsSoldMoney')} ₪{Number(organizer.totalTicketsSoldMoney ?? 0).toLocaleString()}</Typography>
          <Typography variant="caption">{translate('gridOrganizersTotalTicketsSoldFee')} ₪{Number(organizer.totalTicketSoldFee ?? 0).toLocaleString()}</Typography>
          <Typography variant="caption">{translate('gridOrganizersActivePeriodTotalTicketsSoldCount')} {organizer.activePeriodTotalTicketSoldCount ?? 0}</Typography>
          <Typography variant="caption">{translate('gridOrganizersActivePeriodTotalTicketsSoldMoney')} ₪{Number(organizer.activePeriodTotalTicketsSoldMoney ?? 0).toLocaleString()}</Typography>
          <Typography variant="caption">{translate('gridOrganizersActivePeriodTotalTicketsSoldFee')} ₪{Number(organizer.activePeriodTotalTicketSoldFee ?? 0).toLocaleString()}</Typography>
          <Box sx={{ borderTop: '1px dashed rgba(255,255,255,0.3)', my: 0.5 }} />
          <Typography variant="caption">
            {translate('gridOrganizersJoinDate')} {organizer.createDate ? format(new Date(organizer.createDate), 'dd/MM/yyyy HH:mm') : '-'}
          </Typography>
          <Typography variant="caption">
            {translate('gridOrganizersLastEventDate')} {organizer.lastEventDate ? format(new Date(organizer.lastEventDate), 'dd/MM/yyyy HH:mm') : '-'}
          </Typography>
          <Typography variant="caption">
            {translate('gridOrganizersStatus')}{' '}
            {organizer.isActive
              ? translate('gridOrganizersStatusActive')
              : translate('gridOrganizersStatusNotActive')}
          </Typography>
        </>
      )}
    </Box>
  );

  return (
    <Tooltip
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      title={content}
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: '#333',
            color: '#fff',
            maxWidth: 360,
            p: 1.5,
            '& .MuiTypography-root': { color: '#fff' },
          },
        },
        arrow: { sx: { color: '#333' } },
      }}
    >
      <IconButton size="small" color="warning" onClick={handleClick} sx={{ p: 0.25 }}>
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

export default OrganizerInfoTooltip;
